/**
 * *Uložiť report* (PRD-0076): a research run's report saved as one investigation.
 *
 * The run remembers the investigation (`reportInvestigationId`), so the cached run
 * and run list are patched at once — finished runs are never refetched on their own
 * (`useHypothesisRun` keeps them fresh forever).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { hypothesesApi } from '@/services/api/hypotheses'
import type { HypothesisRun, SavedResearchReport } from '@/types/hypothesis'
import { syncSavedInvestigation } from './sync-saved-investigation'
import { hypothesisKeys } from './use-hypotheses'

interface SaveResearchReportVars {
  hypothesisId: string
  runId: string
}

export function useSaveResearchReport(caseId: string) {
  const qc = useQueryClient()
  return useMutation<SavedResearchReport, Error, SaveResearchReportVars>({
    mutationFn: ({ hypothesisId, runId }) => hypothesesApi.saveReport(caseId, hypothesisId, runId),
    onSuccess: (saved, { hypothesisId, runId }) => {
      const linked = (run: HypothesisRun): HypothesisRun =>
        run.id === runId ? { ...run, reportInvestigationId: saved.investigation.id } : run
      qc.setQueryData<HypothesisRun>(hypothesisKeys.run(caseId, hypothesisId, runId), run =>
        run ? linked(run) : run
      )
      qc.setQueryData<HypothesisRun[]>(hypothesisKeys.runs(caseId, hypothesisId), runs =>
        runs?.map(linked)
      )
      syncSavedInvestigation(qc, caseId, saved.investigation)
    },
  })
}
